import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, Card, Loading, Empty, SectionBadge } from '@/components/UI';
import { Plus, Edit2, Trash2, Save, X, Search } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = { name: '', aliases: '', source: 'Manual', active: true, ex_works: false, notes: '' };

export default function CustomerMaster() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null); // { id?, form }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/customers');
      setRows(r.data || []);
    } catch (e) {
      toast.error('Failed to load customers');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startAdd = () => setEditing({ id: null, form: { ...EMPTY_FORM } });
  const startEdit = (c) => setEditing({
    id: c.id || c._id,
    form: {
      name: c.name || '',
      aliases: (c.aliases || []).join(', '),
      source: c.source || 'Manual',
      active: c.active !== false,
      ex_works: c.ex_works === true,
      notes: c.notes || '',
    },
  });
  const cancel = () => setEditing(null);

  const save = async () => {
    const { form, id } = editing;
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const body = {
      name: form.name.trim(),
      aliases: form.aliases.split(',').map((s) => s.trim()).filter(Boolean),
      source: form.source || 'Manual',
      active: !!form.active,
      ex_works: !!form.ex_works,
      notes: form.notes || '',
    };
    try {
      if (id) {
        await api.put(`/customers/${id}`, body);
        toast.success('Customer updated');
      } else {
        await api.post('/customers', body);
        toast.success('Customer created');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"?`)) return;
    try {
      await api.delete(`/customers/${c.id || c._id}`);
      toast.success('Customer deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleActive = async (c) => {
    const id = c.id || c._id;
    try {
      await api.put(`/customers/${id}`, {
        name: c.name,
        aliases: c.aliases || [],
        source: c.source || 'Manual',
        active: !c.active,
        ex_works: c.ex_works === true,
        notes: c.notes || '',
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle');
    }
  };

  const filtered = rows.filter((c) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.aliases || []).some((a) => a.toLowerCase().includes(q))
    );
  });

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Master"
        subtitle="Single source of truth for customer names + aliases used to match ERP rows"
        actions={
          isAdmin && (
            <button className="btn-primary" onClick={startAdd} data-testid="btn-add-customer">
              <Plus size={14} className="inline mr-1" /> Add Customer
            </button>
          )
        }
      />

      <Card
        title={`Customers (${rows.length})`}
        action={
          <div className="flex items-center gap-2">
            <Search size={14} className="text-slate-400" />
            <input
              type="text"
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-[var(--aampl-border)] rounded px-2 py-1"
              data-testid="customer-filter"
            />
          </div>
        }
      >
        {filtered.length === 0 ? (
          <Empty title="No customers" hint="Add your first customer." />
        ) : (
          <div className="aampl-table-wrap">
            <table className="aampl-table" data-testid="customers-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Aliases (ERP match)</th>
                  <th>Source</th>
                  <th>Freight</th>
                  <th>Status</th>
                  {isAdmin && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id || c._id}>
                    <td className="font-medium">{c.name}</td>
                    <td className="text-xs text-slate-600">{(c.aliases || []).join(', ') || '—'}</td>
                    <td className="text-xs">{c.source}</td>
                    <td className="text-xs" data-testid={`freight-${c.name.replace(/\s+/g, '-').toLowerCase()}`}>{c.ex_works ? 'Self-pickup' : 'We ship'}</td>
                    <td>
                      <SectionBadge status={c.active ? 'green' : 'red'} label={c.active ? 'Active' : 'Inactive'} />
                    </td>
                    {isAdmin && (
                      <td className="text-right whitespace-nowrap">
                        <button
                          className="btn-ghost text-xs mr-1"
                          onClick={() => toggleActive(c)}
                          data-testid={`btn-toggle-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {c.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="btn-ghost text-xs mr-1"
                          onClick={() => startEdit(c)}
                          data-testid={`btn-edit-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <Edit2 size={12} className="inline" /> Edit
                        </button>
                        <button
                          className="text-xs text-red-700 hover:underline"
                          onClick={() => remove(c)}
                          data-testid={`btn-delete-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <Trash2 size={12} className="inline" /> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isAdmin && (
          <div className="text-[11px] text-slate-500 mt-3">
            Read-only — only administrators can edit the Customer Master.
          </div>
        )}
      </Card>

      {/* Edit / Add modal */}
      {editing && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          data-testid="customer-edit-modal"
          onClick={cancel}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold aampl-navy-text">
                {editing.id ? 'Edit Customer' : 'Add Customer'}
              </h3>
              <button className="btn-ghost" onClick={cancel} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Name *</label>
                <input
                  type="text"
                  className="w-full border border-[var(--aampl-border)] rounded px-3 py-2 text-sm"
                  value={editing.form.name}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })}
                  data-testid="input-customer-name"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
                  Aliases (comma-separated, used to match ERP "Customer" cells)
                </label>
                <input
                  type="text"
                  className="w-full border border-[var(--aampl-border)] rounded px-3 py-2 text-sm"
                  placeholder="e.g. astemo india, astemo fie"
                  value={editing.form.aliases}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, aliases: e.target.value } })}
                  data-testid="input-customer-aliases"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Lower-cased substring match. Add every spelling/plant variant you want rolled up.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Source</label>
                  <select
                    className="w-full border border-[var(--aampl-border)] rounded px-3 py-2 text-sm"
                    value={editing.form.source}
                    onChange={(e) => setEditing({ ...editing, form: { ...editing.form, source: e.target.value } })}
                  >
                    <option>Manual</option>
                    <option>Production</option>
                    <option>ERP</option>
                    <option>Production + ERP</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editing.form.active}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, active: e.target.checked } })}
                      data-testid="input-customer-active"
                    />
                    Active
                  </label>
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.form.ex_works}
                    onChange={(e) => setEditing({ ...editing, form: { ...editing.form, ex_works: e.target.checked } })}
                    data-testid="input-customer-exworks"
                  />
                  Self-pickup / Ex-works (no freight)
                </label>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Notes</label>
                <textarea
                  className="w-full border border-[var(--aampl-border)] rounded px-3 py-2 text-sm"
                  rows={2}
                  value={editing.form.notes}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, notes: e.target.value } })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-[var(--aampl-border)]">
              <button className="btn-ghost" onClick={cancel}>Cancel</button>
              <button className="btn-primary" onClick={save} data-testid="btn-customer-save">
                <Save size={14} className="inline mr-1" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
